/**
 * Location Map Alpine Component
 * Displays a map with a center marker and nearby listings within 50 miles
 */

import Alpine from 'alpinejs';
import { createMap, mapboxgl } from '../utils/mapbox.js';
import {
    isValidCoord,
    formatPrice,
    getListingKey,
    getListingDisplayPrice,
    calculatePopupAnchor,
    ensurePopupVisible as sharedEnsurePopupVisible,
    POPUP_GAP,
    DEFAULT_MARKER_HEIGHT,
    POPUP_BASE_OPTIONS,
} from '../utils/mapHelpers.js';

Alpine.data('locationMap', (config = {}) => ({
    map: null,
    popup: null,
    popupCache: new Map(),
    priceMarkersMap: new Map(),
    loaded: false,
    ready: false,
    listings: [],
    currentPopupListing: null,
    currentPopupCoordinates: null,
    windowResizeHandler: null,

    // Config with defaults
    center: config.center || [0, 0],
    zoom: config.zoom || 10,
    radius: config.radius || 50,
    market: config.market || null,

    init() {
        this.$nextTick(() => {
            this.initMap();
        });

        // Add window resize handler for popup width
        this.windowResizeHandler = () => this.updatePopupWidth();
        window.addEventListener('resize', this.windowResizeHandler);
    },

    async initMap() {
        this.map = createMap(this.$refs.map, {
            center: this.center,
            zoom: this.zoom,
        });

        if (!this.map) return;

        this.map.on('load', () => {
            this.loaded = true;

            this.setupMapListeners();
            this.fetchNearbyListings();
        });
    },

    /**
     * Calculate max bounds around center
     * Rough approximation: 1 degree latitude ≈ 69 miles
     */
    calculateMaxBounds(center, radiusMiles) {
        const [lng, lat] = center;
        const degreesLat = radiusMiles / 69;
        const degreesLng = radiusMiles / (69 * Math.cos(lat * Math.PI / 180));

        return [
            [lng - degreesLng, lat - degreesLat], // Southwest
            [lng + degreesLng, lat + degreesLat], // Northeast
        ];
    },

    /**
     * Haversine distance between two points in miles
     */
    haversineDistance(lat1, lng1, lat2, lng2) {
        const R = 3958.8; // Earth radius in miles
        const dLat = (lat2 - lat1) * Math.PI / 180;
        const dLng = (lng2 - lng1) * Math.PI / 180;
        const a =
            Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
            Math.sin(dLng / 2) * Math.sin(dLng / 2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        return R * c;
    },

    async fetchNearbyListings() {
        try {
            const [lng, lat] = this.center;
            const isResort = !!this.market;
            const params = new URLSearchParams({
                lat,
                lng,
                limit: isResort ? 500 : 50,
                radiusMiles: this.radius,
            });
            // When scoped to a resort, pass the market name so the server filters by relatedResort.
            if (isResort) {
                params.set('market', this.market);
            }
            const response = await fetch(`/actions/guesty/search/index?${params.toString()}`);
            const data = await response.json();

            if (data.success && data.results) {
                const received = data.results.length;
                this.listings = data.results
                    .filter(listing => {
                        if (!listing.lat || !listing.lng || !isValidCoord(listing.lng, listing.lat)) {
                            return false;
                        }
                        // Resort searches return only related listings; skip radius cutoff so we don't drop any.
                        if (isResort) return true;
                        return this.haversineDistance(lat, lng, listing.lat, listing.lng) <= this.radius;
                    });

                // Cap visible markers only on radius searches; show all when scoped to a resort.
                if (!isResort) {
                    this.listings = this.listings.slice(0, 20);
                }

                console.log('[locationMap] market=%o received=%d kept=%d rendered=%d serverCount=%o',
                    this.market, received, this.listings.length, this.listings.length, data.count);

                this.updateMarkers();
                this.fitToListings();
            }
        } catch (error) {
            console.error('Error fetching nearby listings:', error);
        } finally {
            this.ready = true;
        }
    },

    setupMapListeners() {
        // Close popup when clicking empty map area
        this.map.on('click', () => {
            if (!this.popup || this._popupJustOpened) return;
            this.popup.remove();
        });
    },

    updateMarkers() {
        if (!this.map || !this.loaded) return;
        this.createPriceMarkers(this.listings);
    },

    /**
     * Fit map bounds to show all filtered listings plus the center point
     */
    fitToListings() {
        if (!this.listings.length) return;

        const bounds = new mapboxgl.LngLatBounds();
        bounds.extend(this.center);

        this.listings.forEach(listing => {
            bounds.extend([listing.lng, listing.lat]);
        });

        this.map.fitBounds(bounds, {
            padding: 60,
            maxZoom: 14,
            duration: 0,
        });

        // Constrain panning to the listing area (with padding)
        const listingBounds = this.map.getBounds();
        const lngPad = (listingBounds.getEast() - listingBounds.getWest()) * 0.3;
        const latPad = (listingBounds.getNorth() - listingBounds.getSouth()) * 0.3;
        this.map.setMaxBounds([
            [listingBounds.getWest() - lngPad, listingBounds.getSouth() - latPad],
            [listingBounds.getEast() + lngPad, listingBounds.getNorth() + latPad],
        ]);
    },

    createPriceMarkers(listings) {
        // Clear popup cache when listings change
        this.popupCache.clear();

        // Remove old markers that are no longer in listings
        const newIds = new Set(listings.map((l) => getListingKey(l)));
        for (const [id, marker] of this.priceMarkersMap) {
            if (!newIds.has(id)) {
                marker.remove();
                this.priceMarkersMap.delete(id);
            }
        }

        // Create markers for new listings
        listings.forEach((listing) => {
            const listingKey = getListingKey(listing);
            if (this.priceMarkersMap.has(listingKey)) return;

            const el = this.createPriceMarkerElement(listing);

            const marker = new mapboxgl.Marker({
                element: el,
                anchor: 'bottom',
            })
                .setLngLat([listing.lng, listing.lat])
                .addTo(this.map);

            this.priceMarkersMap.set(listingKey, marker);
        });
    },

    createPriceMarkerElement(listing) {
        const el = document.createElement('div');
        el.className = 'search-map-price-marker';
        el.innerHTML = `
            <span class="bg-av-dark-brown/60 min-w-8 flex items-center justify-center text-av-cream border border-white py-1 px-1.75 text-[0.875rem] tracking-wide font-semibold rounded-full whitespace-nowrap transition-all duration-200 hover:bg-av-dark-brown hover:text-white">
                ${formatPrice(listing.basePrice) || '&nbsp;'}
            </span>
        `;

        const listingKey = getListingKey(listing);
        el.dataset.listingId = listingKey;

        // Add click handler
        el.addEventListener('click', (e) => {
            e.stopPropagation();
            this.showPopup(listing, [listing.lng, listing.lat]);
        });

        return el;
    },

    showPopup(listing, coordinates) {
        const listingKey = getListingKey(listing);
        const currentKey = this.currentPopupListing ? getListingKey(this.currentPopupListing) : null;

        // If popup already open for this listing, just update width
        if (this.popup && currentKey === listingKey) {
            this.updatePopupWidth();
            return;
        }

        // Remove current popup
        if (this.popup) {
            this.popup.remove();
        }

        // Store current listing for resize updates
        this.currentPopupListing = listing;
        this.currentPopupCoordinates = coordinates;

        // Measure marker height for popup offset
        const markerInstance = this.priceMarkersMap.get(listingKey);
        const markerHeight = markerInstance?.getElement()?.offsetHeight || DEFAULT_MARKER_HEIGHT;

        // Store for flip logic
        this._popupGap = POPUP_GAP;
        this._popupMarkerHeight = markerHeight;

        // Pre-calculate best anchor based on available screen space
        const { anchor, offset: popupOffset } = calculatePopupAnchor({
            map: this.map,
            coordinates,
            markerHeight,
            gap: POPUP_GAP,
            lastPopupHeight: this._lastPopupHeight,
        });

        // Check cache first
        if (this.popupCache.has(listingKey)) {
            this.popup = this.popupCache.get(listingKey);
            this.popup.options.anchor = anchor;
            this.popup.options.offset = popupOffset;
            this.popup.setLngLat(coordinates).addTo(this.map);
            this.hydratePopup(listing);
        } else {
            // Create new from server-rendered HTML
            this.popup = new mapboxgl.Popup({
                ...POPUP_BASE_OPTIONS,
                anchor,
                offset: popupOffset,
            })
                .setLngLat(coordinates)
                .setHTML(listing.popupHtml)
                .addTo(this.map);

            // Add to cache
            this.popupCache.set(listingKey, this.popup);

            // Listen for close event
            this.popup.on('close', () => {
                if (this._suppressClose) return;

                if (this.currentPopupListing) {
                    const closedKey = getListingKey(this.currentPopupListing);
                    if (closedKey === listingKey) {
                        this.popup = null;
                        this.currentPopupListing = null;
                        this.currentPopupCoordinates = null;
                    }
                }
            });

            // Add event listeners (only on creation — they persist on cached instances)
            const popupEl = this.popup.getElement();
            if (popupEl) {
                popupEl.style.pointerEvents = 'auto';

                // Heart button handler
                const heartBtn = popupEl.querySelector('.search-map-popup-heart');
                if (heartBtn) {
                    heartBtn.addEventListener('click', (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        const lid = parseInt(heartBtn.dataset.listingId, 10);

                        if (!this.$store.user?.isFetched) return;
                        if (!this.$store.user?.isLoggedIn) {
                            this.$store.modals?.open?.('sign-in-modal');
                            return;
                        }
                        this.$store.wishlistModal?.open?.(lid);
                    });
                }
            }

            this.hydratePopup(listing);
        }

        // Prevent the map click handler from closing the popup immediately
        this._popupJustOpened = true;
        setTimeout(() => { this._popupJustOpened = false; }, 0);

        // Verify popup position after render
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                if (this.popup) {
                    this.ensurePopupVisible(coordinates);
                }
            });
        });
    },

    ensurePopupVisible(coordinates) {
        if (!this.map || !this.popup) return;

        const { measuredHeight } = sharedEnsurePopupVisible({
            map: this.map,
            popup: this.popup,
            coordinates,
            gap: this._popupGap,
            markerHeight: this._popupMarkerHeight,
            setSuppressClose: (val) => { this._suppressClose = val; },
        });

        if (measuredHeight > 0) {
            this._lastPopupHeight = measuredHeight;
        }
    },

    updatePopupWidth() {
        if (!this.popup || !this.currentPopupListing) return;

        const cardWidth = 280;
        const popupCard = this.popup.getElement()?.querySelector('.search-map-popup-card');
        if (popupCard && cardWidth) {
            popupCard.style.width = `${cardWidth}px`;
            if (this.currentPopupCoordinates) {
                this.popup.setLngLat(this.currentPopupCoordinates);
            }
        }
    },

    /**
     * Hydrate a server-rendered popup card with dynamic data.
     * Called after every popup show (new or cached) to ensure price and wishlist state are current.
     */
    hydratePopup(listing) {
        const popupEl = this.popup?.getElement();
        if (!popupEl) return;

        // Set fixed card width
        const card = popupEl.querySelector('.search-map-popup-card');
        if (card) {
            card.style.width = '280px';
        }

        // Update price (always base price on location pages)
        const priceEl = popupEl.querySelector('[data-popup-price]');
        if (priceEl) {
            priceEl.textContent = formatPrice(getListingDisplayPrice(listing, null));
        }

        // Update wishlist state
        const listingId = listing.id || listing.guestyListingId;
        const heartBtn = popupEl.querySelector('.search-map-popup-heart');
        if (heartBtn) {
            const isFavorited = this.$store.wishlist?.has?.(listingId);
            heartBtn.classList.toggle('is-active', !!isFavorited);
        }
    },

    destroy() {
        if (this.windowResizeHandler) {
            window.removeEventListener('resize', this.windowResizeHandler);
            this.windowResizeHandler = null;
        }
        if (this.popup) {
            this.popup.remove();
            this.popup = null;
        }
        for (const marker of this.priceMarkersMap.values()) {
            marker.remove();
        }
        this.priceMarkersMap.clear();
        if (this.map) {
            this.map.remove();
        }
    },
}));
